import { DIMENSION_LABELS } from '@shared/config/dimensions.js';
import { Section, MetricCard } from '@/components/ui/index.js';
import { RadarChart, LineChart, HeatmapChart, BarChart } from '@/components/charts/index.js';
import type { UserAnalytics } from '@/lib/types.js';

/**
 * Analytics block on the user detail page — sync'd training data plus
 * login activity rolled into one card with metric strip + 4 charts.
 *
 * Pulled out of UserDetailPage.tsx so the page-level orchestrator stays
 * under 300 lines. Self-contained: takes raw analytics + fmt helpers,
 * returns a section.
 */

// 维度标签直接复用 shared DIMENSION_LABELS — 单一真相源, 改 shared 自动同步.
const DIM_LABEL = DIMENSION_LABELS;

const RANK_COLOR: Record<string, string> = {
  S: 'text-amber-300',
  A: 'text-rose-300',
  B: 'text-indigo-300',
  C: 'text-emerald-300',
  D: 'text-slate-400',
};

interface Props {
  analytics: UserAnalytics;
  fmtDate: (s: string | null | undefined) => string;
  fmtDateTime: (s: string | null | undefined) => string;
}

export function AnalyticsSection({ analytics, fmtDate, fmtDateTime }: Props): JSX.Element {
  const {
    latest_assessment,
    assessment_timeline,
    attempts_per_day,
    dimension_stats,
    login_heatmap,
    totals,
  } = analytics;

  const hasAnyData =
    totals.total_assessments > 0 ||
    totals.total_attempts > 0 ||
    totals.total_logins > 0;

  if (!hasAnyData) {
    return (
      <Section title="数据分析" emoji="📊" className="mb-6">
        <p className="text-sm text-slate-500">该用户暂未产生任何训练 / 登录数据</p>
      </Section>
    );
  }

  // Composite-score timeline points (server gives ASC ordering, ≤50 entries).
  const compositePoints = assessment_timeline.map((a, i) => ({
    label: `#${i + 1}`,
    y: a.composite_score,
  }));

  // Daily accuracy over last 90 days.
  const accuracyPoints = attempts_per_day.map((d) => ({
    label: d.date.slice(5),
    y: d.count === 0 ? 0 : (d.correct_count / d.count) * 100,
  }));

  // 条形按"正确率"画长度 — 早期把 value 设成 attempts 让长度 = 答题量,
  // 但 hint 显示正确率, 视觉上"条长 = 好"跟实际语义打架. 现在统一:
  //   - bar 长度 = 正确率 (0-100%)
  //   - bar 内文字 = "88% 正确"
  //   - hint = "25 题" (答题量保留为辅助信息)
  const dimBars = dimension_stats.map((d) => {
    const pct = d.attempts > 0 ? d.accuracy * 100 : 0;
    // exactOptionalPropertyTypes: true 不允许 hint: undefined; 用 spread
    // 让 0 题的维度完全不带 hint 字段, 而不是带个 undefined.
    return {
      label: DIM_LABEL[d.dimension],
      value: pct,
      valueLabel: d.attempts > 0 ? `${pct.toFixed(0)}% 正确` : '暂无',
      max: 100,
      ...(d.attempts > 0 ? { hint: `${d.attempts} 题` } : {}),
    };
  });

  return (
    <Section title="数据分析" emoji="📊" className="mb-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <MetricCard label="累计测评" value={totals.total_assessments} unit="次" />
        <MetricCard label="累计答题" value={totals.total_attempts} unit="题" />
        <MetricCard label="累计登录" value={totals.total_logins} unit="次" />
        <MetricCard
          label="训练时长"
          value={(totals.total_training_seconds / 60).toFixed(0)}
          unit="分钟"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-5 mb-6">
        <div>
          <h3 className="text-xs font-semibold text-slate-400 mb-2">
            能力雷达
            {latest_assessment !== null && (
              <span className="ml-2 text-[10px] text-slate-500 font-normal">
                · 最近测评 {fmtDate(latest_assessment.completed_at)}
                <span className={`ml-2 ${RANK_COLOR[latest_assessment.rank_tier] ?? ''}`}>
                  {latest_assessment.rank_tier} 段
                </span>
              </span>
            )}
          </h3>
          {latest_assessment !== null ? (
            <RadarChart values={latest_assessment.dimension_scores} />
          ) : (
            <div className="h-[220px] flex items-center justify-center text-xs text-slate-500">
              暂无测评
            </div>
          )}
        </div>
        <div>
          <h3 className="text-xs font-semibold text-slate-400 mb-2">分维度答题量</h3>
          <BarChart bars={dimBars} emptyMsg="暂无答题" />
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-xs font-semibold text-slate-400 mb-2">
          综合分变化（历次测评）
        </h3>
        {/* 品牌色: shark-400(蓝)。脱离裸 sky-400, 见设计纪律「图表脱品牌色归位」。 */}
        <LineChart points={compositePoints} yMax={100} yMin={0} color="#60a5fa" />
      </div>

      <div className="mb-6">
        <h3 className="text-xs font-semibold text-slate-400 mb-2">
          每日答题正确率（近 90 天）
        </h3>
        {/* 品牌色: felt-400(薄荷=数据)。去掉被设计纪律点名要删的紫色。 */}
        <LineChart points={accuracyPoints} yMax={100} yMin={0} color="#34d399" />
      </div>

      <div>
        <h3 className="text-xs font-semibold text-slate-400 mb-2">
          登录时段热力图（近 90 天 · UTC）
        </h3>
        <HeatmapChart cells={login_heatmap} />
      </div>

      {latest_assessment !== null && (
        <p className="text-[10px] text-slate-600 mt-4">
          最近测评来源 = {latest_assessment.source} · 完成于{' '}
          {fmtDateTime(latest_assessment.completed_at)}
        </p>
      )}
    </Section>
  );
}
